import { DateTime as DT } from 'luxon';
import { TaskTypeStatus } from '@econtractor/types';
import { useTranslation } from 'react-i18next';
import EToolTip from 'components/others/EToolTip';
import TaskTimeProgressCircle from './TaskTimeProgressCircle';
import SolarIcon from 'components/icons/SolarIcon';

export interface TaskTermIntervalProps {
    taskId: number;
    dateStart?: number | null;
    dateEnd?: number | null;
    taskStatus: number;
    withTime?: boolean;
}

type TermSideSlot = 'progress' | 'hourglass' | 'check' | 'overdue' | 'cancelled';

const formatTermPoint = (millis: number, withTime = false): string => {
    const dt = DT.fromMillis(millis, { zone: 'local' });
    if (!dt.isValid) return '—';
    return withTime ? dt.toFormat('dd.MM.yyyy HH:mm') : dt.toFormat('dd.MM.yyyy');
};

const toMillis = (value: unknown): number => {
    if (value == null || value === '') return 0;
    if (typeof value === 'number' && Number.isFinite(value) && value > 0) return value;
    if (typeof value === 'bigint') return Number(value);
    if (value instanceof Date) {
        const ms = value.getTime();
        return Number.isFinite(ms) ? ms : 0;
    }
    const raw = String(value).trim();
    if (!raw) return 0;
    const asNum = Number(raw);
    if (Number.isFinite(asNum) && asNum > 1e11) return asNum;
    const iso = DT.fromISO(raw, { zone: 'local' });
    if (iso.isValid) return iso.toMillis();
    const sql = DT.fromSQL(raw, { zone: 'local' });
    return sql.isValid ? sql.toMillis() : 0;
};

const TermSideIcon = ({
    kind,
    title,
}: {
    kind: Exclude<TermSideSlot, 'progress'>;
    title: string;
}) => {
    const iconClass =
        kind === 'hourglass'
            ? 'bi-hourglass-split'
            : kind === 'check'
              ? 'bi-check-lg'
              : kind === 'cancelled'
                ? 'bi-x-lg'
                : 'bi-x-lg';

    return (
        <EToolTip title={title}>
            <span className={`task-term-side-icon task-term-side-icon--${kind}`} aria-label={title}>
                <SolarIcon name={iconClass} />
            </span>
        </EToolTip>
    );
};

/**
 * Період виконання: date_start ↓ date_end (вертикально) + слот стану.
 */
const TaskTermInterval = ({
    taskId,
    dateStart,
    dateEnd,
    taskStatus,
    withTime = false,
}: TaskTermIntervalProps) => {
    const { t } = useTranslation(['Tasks']);
    const now = Date.now();

    const endRaw = toMillis(dateEnd);
    if (!endRaw) {
        return <span className="text-muted">—</span>;
    }

    const endDt = DT.fromMillis(endRaw, { zone: 'local' });
    if (!endDt.isValid) {
        return <span className="text-muted">—</span>;
    }

    const startRaw = toMillis(dateStart);
    const startDt = startRaw
        ? DT.fromMillis(startRaw, { zone: 'local' })
        : endDt.startOf('day');
    if (!startDt.isValid) {
        return <span className="text-muted">—</span>;
    }

    const startMs = startDt > endDt ? DT.now().startOf('day').toMillis() : startDt.toMillis();
    const endMs = endDt.toMillis();
    const hasRange = endMs > startMs;
    const inRange = hasRange && now >= startMs && now <= endMs;

    const elapsedPercent = inRange ? ((now - startMs) / (endMs - startMs)) * 100 : 0;
    const daysLeft = inRange
        ? Math.max(0, Math.ceil((endMs - now) / (24 * 60 * 60 * 1000)))
        : undefined;

    const isCancelled = taskStatus === TaskTypeStatus.CANCELLED;
    const isInactive =
        isCancelled || taskStatus === TaskTypeStatus.REJECT;
    const isDone =
        taskStatus === TaskTypeStatus.COMPLETED || taskStatus === TaskTypeStatus.ACCEPTED;
    const overdueOpen = !isInactive && !isDone && now > endMs;
    const awaitingExecution = !isInactive && !isDone && !overdueOpen && now < startMs;
    const canShowProgress = !isInactive && !isDone && !overdueOpen && inRange;

    let sideSlot: TermSideSlot | null = null;
    if (isCancelled) {
        sideSlot = 'cancelled';
    } else if (!isInactive) {
        if (isDone) sideSlot = 'check';
        else if (overdueOpen) sideSlot = 'overdue';
        else if (awaitingExecution) sideSlot = 'hourglass';
        else if (canShowProgress) sideSlot = 'progress';
    }

    const startLabel = formatTermPoint(startMs, false);
    const endLabel = formatTermPoint(endMs, withTime);

    return (
        <div
            className="task-term-interval"
            style={{
                position: 'relative',
                width: '100%',
                minWidth: 0,
                paddingLeft: sideSlot ? 42 : 0,
                boxSizing: 'border-box',
                overflow: 'visible',
            }}
        >
            {/* pre-line + \n — єдиний надійний спосіб обійти td { white-space:nowrap } */}
            <div
                className="task-term-interval__dates"
                style={{
                    whiteSpace: 'pre-line',
                    lineHeight: 1.3,
                    fontSize: '0.75rem',
                    overflow: 'visible',
                }}
            >
                <span className="task-term-interval__start" style={{ opacity: 0.7 }}>
                    {startLabel}
                </span>
                {'\n'}
                <span className="task-term-interval__arrow" style={{ opacity: 0.55 }}>
                    ↓
                </span>
                {'\n'}
                <strong className="task-term-interval__end" style={{ fontWeight: 600 }}>
                    {endLabel}
                </strong>
            </div>

            {sideSlot ? (
                <div
                    className="task-term-interval__side"
                    style={{
                        position: 'absolute',
                        top: '50%',
                        left: 0,
                        transform: 'translateY(-50%)',
                    }}
                >
                    {sideSlot === 'progress' && (
                        <TaskTimeProgressCircle
                            taskId={taskId}
                            elapsedPercent={elapsedPercent}
                            days={daysLeft}
                        />
                    )}
                    {sideSlot === 'hourglass' && (
                        <TermSideIcon kind="hourglass" title={t('term.notStarted')} />
                    )}
                    {sideSlot === 'check' && (
                        <TermSideIcon kind="check" title={t('term.completed')} />
                    )}
                    {sideSlot === 'overdue' && (
                        <TermSideIcon kind="overdue" title={t('term.overdue')} />
                    )}
                    {sideSlot === 'cancelled' && (
                        <TermSideIcon kind="cancelled" title={t('status.3')} />
                    )}
                </div>
            ) : null}
        </div>
    );
};

export default TaskTermInterval;
